import { LineItem } from './types';

export type StaffingMode = 'LINE_TOTAL' | 'EXPLICIT_TOTAL';
export const parseQuoteObject = (value: any): any => {
  if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return {}; } }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};
export const quoteItems = (value: any): LineItem[] => {
  if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return []; } }
  return Array.isArray(value) ? value : [];
};
export const quoteNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
export const isAcceptedQuote = (quote: any): boolean =>
  ['APPROVED', 'COMPLETED', 'COMPLETE'].includes(String(quote?.status).toUpperCase());

// A selection is explicit: simulations are never implicitly persisted as purchases.
export const includedQuoteItems = (items: LineItem[], selected = new Set<string>()): LineItem[] =>
  items.filter(item => !item.isOptional || selected.has(item.id));
export const linePrice = (item: LineItem): number =>
  quoteNumber(item.price) * quoteNumber(item.quantity) * (1 - quoteNumber(item.discount) / 100);
export const quotePricing = (items: LineItem[]) => {
  const gross = items.reduce((sum, item) => sum + quoteNumber(item.price) * quoteNumber(item.quantity), 0);
  const subtotal = items.reduce((sum, item) => sum + linePrice(item), 0);
  const gst = subtotal * 0.15;
  return { gross, discount: gross - subtotal, subtotal, gst, total: subtotal + gst };
};

export const staffingPolicy = (quote: any): { version: 1; mode: StaffingMode } | null => {
  const p = parseQuoteObject(quote?.logistics).quoteStaffing;
  return p?.version === 1 && ['LINE_TOTAL', 'EXPLICIT_TOTAL'].includes(p.mode) ? p : null;
};
export const withStaffingPolicy = (logistics: any, mode: StaffingMode) => ({
  ...parseQuoteObject(logistics), quoteStaffing: { version: 1, mode },
});
export const quoteStaffing = (quote: any, included = includedQuoteItems(quoteItems(quote?.items))) => {
  const lineTotal = included.reduce((sum, item) => sum + Math.max(0, quoteNumber(item.staffRequired)), 0);
  const savedTotal = Math.max(0, quoteNumber(quote?.staff_count_required ?? quote?.staffCountRequired));
  const policy = staffingPolicy(quote);
  // Legacy totals are never added to lines or rewritten on read. An Admin must
  // resolve mixed legacy values; there is no safe automatic historical backfill.
  const total = policy?.mode === 'LINE_TOTAL' ? lineTotal : policy?.mode === 'EXPLICIT_TOTAL' ? savedTotal : savedTotal || lineTotal;
  return { total, lineTotal, savedTotal, needsReview: !policy && savedTotal > 0 && lineTotal > 0 };
};
export const requiresCalculationReview = (quote: any): boolean =>
  !staffingPolicy(quote) && (quoteStaffing(quote).needsReview ||
    quoteItems(quote?.items).some(item => quoteNumber(item.discount) !== 0));

export const quoteHours = (setup?: string, packDown?: string): number => {
  if (!setup || !packDown) return 0;
  const minutes = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  };
  let end = minutes(packDown); const start = minutes(setup);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  if (end < start) end += 1440;
  return (end - start) / 60;
};
export const quoteLabour = (quote: any, included?: LineItem[]) => {
  const costs = parseQuoteObject(quote?.internal_costs ?? parseQuoteObject(quote?.logistics).internalCosts);
  const hours = quoteHours(quote?.setup_time ?? quote?.setupTime, quote?.pack_down_time ?? quote?.packDownTime);
  const staff = quoteStaffing(quote, included).total;
  const rate = quoteNumber(costs.laborRatePerHour ?? 35);
  return { hours, staff, rate, cost: hours * staff * rate };
};

// Used only for an explicit draft save/approval. No migration, background write,
// Shift write, or historical repricing is performed by this pure calculation.
export const calculationPatch = (quote: any, selected = new Set<string>()) => {
  if (isAcceptedQuote(quote)) throw new Error('Accepted Quote totals are preserved. Duplicate to a draft revision before changing calculations.');
  if (requiresCalculationReview(quote)) throw new Error('Admin calculation review required. Open the draft editor and review staffing and percentage discounts before approval.');
  const items = quoteItems(quote.items).map(item => ({ ...item, isOptional: selected.has(item.id) ? false : item.isOptional }));
  const included = includedQuoteItems(items);
  const pricing = quotePricing(included);
  const staffing = quoteStaffing(quote, included);
  const mode = staffingPolicy(quote)?.mode ?? (staffing.savedTotal > 0 ? 'EXPLICIT_TOTAL' : 'LINE_TOTAL');
  return {
    items, subtotal: pricing.subtotal, grand_total: pricing.total, total_amount: pricing.total, gst_amount: pricing.gst,
    staff_count_required: staffing.total,
    logistics: withStaffingPolicy(quote.logistics, mode),
    staffing_tier: staffing.total > 0 ? 'FULLY_STAFFED' : (quote.staffing_tier ?? quote.staffingTier ?? 'DRY_HIRE'),
  };
};

export const displayQuotePricing = (quote: any, selected = new Set<string>()) => {
  const calculated = quotePricing(includedQuoteItems(quoteItems(quote?.items), selected));
  if (!isAcceptedQuote(quote)) return { ...calculated, preserved: false };
  // Keep historical agreed totals, even when the old percentage bug produced them.
  const subtotal = quote?.subtotal == null ? calculated.subtotal : quoteNumber(quote.subtotal);
  const stored = quote?.grand_total ?? quote?.grandTotal ?? quote?.total_amount;
  const total = stored == null ? subtotal * 1.15 : quoteNumber(stored);
  return { ...calculated, subtotal, total, gst: total - subtotal, preserved: true };
};
